/* eslint-disable max-len */
import { ApiResponseModel } from '../types/ApiResponseModel';
import { DataRequestConfig } from '../types/DataRequestConfig';
import { NonPublicStudentEntryModel } from '../types/NonPublicStudentEntryModel';
import { NonPublicStudentFormViewModel } from '../types/NonPublicStudentFormViewModel';
import { NonPublicStudentListItemModel } from '../types/NonPublicStudentListItemModel';
import { NonPublicStudentScheduleFormViewModel } from '../types/NonPublicStudentScheduleFormViewModel';
import { NonPublicStudentScheduleListItemModel } from '../types/NonPublicStudentScheduleListItemModel';
import { NonPublicStudentScheduleModel } from '../types/NonPublicStudentScheduleModel';
import { NonPublicStudentSearchResultModel } from '../types/NonPublicStudentSearchResultModel';
import { SelectOption } from '../types/SelectOption';
import { getApiBaseUrl } from './app.service';
import { executeApiRequest, getAuthToken } from './base.service';
import { getHttpClient, HttpClient } from './httpClient';

export const nonPublicStudentApiPaths = {
  create: 'nonPublicStudent',
  update: 'nonPublicStudent',
  getSchoolYearListItems: 'nonPublicStudent/getSchoolYearListItems',
  getListItems: (calendarYear: string) =>
    `nonPublicStudent?calendarYear=${calendarYear}`,
  getById: (nonPublicStudentId: string): string =>
    `nonPublicStudent/${nonPublicStudentId}`,
  getStudentByMsisId: (msisId: string): string =>
    `nonPublicStudent/search/${msisId}`,
  getForm: 'nonPublicStudent/form',
  createSchedule: 'nonPublicStudentSchedule',
  updateSchedule: 'nonPublicStudentSchedule',
  getSchedulesByNPSId: (npsId: string): string =>
    `nonPublicStudentSchedule/${npsId}/schedule`,
  getByScheduleId: (scheduleId: string): string =>
    `nonPublicStudentSchedule/${scheduleId}`,
  getScheduleForm: (districtId: string | undefined, calendarYear: string) =>
    `nonpublicstudent/entrysource/form/${districtId}/${calendarYear}`,
  deleteSchedule: (scheduleId: string): string =>
    `nonPublicStudentSchedule/${scheduleId}`,
  deleteStudent: (studentId: string): string => `nonPublicStudent/${studentId}`,
  getStudentByMsisIdInNon200500: (msisId: string): string =>
    `student/${msisId}`,
  getSchoolYears:
    'common/schoolyearsbymodule/formview/NonPublicStudent?showLongYears=true',
};

export const NonPublicStudentServiceClient = (
  config?: DataRequestConfig
): HttpClient =>
  getHttpClient(getApiBaseUrl(), getAuthToken() || '', config?.signal);

export const createNonPublicStudent = async (
  body: NonPublicStudentEntryModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  if (body.schoolYear && body.schoolYear.includes('-')) {
    body.schoolYear = body.schoolYear.split('-')[1].trim();
  }
  
  const formattedBody = { ...body };
  if (formattedBody.personIdentifierSsn) {
    formattedBody.personIdentifierSsn = formattedBody.personIdentifierSsn.replace(/[-\s]/g, '');
  }
  
  return executeApiRequest<string>(async () => {
    return await NonPublicStudentServiceClient(config).post(
      nonPublicStudentApiPaths.create,
      formattedBody
    );
  });
};

export const updateNonPublicStudent = async (
  body: NonPublicStudentEntryModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  if (body.schoolYear && body.schoolYear.includes('-')) {
    body.schoolYear = body.schoolYear.split('-')[1].trim();
  }
  
  const formattedBody = { ...body };
  if (formattedBody.personIdentifierSsn) {
    formattedBody.personIdentifierSsn = formattedBody.personIdentifierSsn.replace(/[-\s]/g, '');
  }
  
  return executeApiRequest<unknown>(async () => {
    return await NonPublicStudentServiceClient(config).put(
      nonPublicStudentApiPaths.update,
      formattedBody
    );
  });
};

export const createNonPublicStudentSchedule = async (
  body: NonPublicStudentScheduleModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    return await NonPublicStudentServiceClient(config).post(
      nonPublicStudentApiPaths.createSchedule,
      body
    );
  });
};

export const updateNonPublicStudentSchedule = async (
  body: NonPublicStudentScheduleModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  return executeApiRequest<unknown>(async () => {
    return await NonPublicStudentServiceClient(config).put(
      nonPublicStudentApiPaths.updateSchedule,
      body
    );
  });
};

export const getNonPublicStudentListItems = async (
  calendarYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<NonPublicStudentListItemModel[]>> => {
  return executeApiRequest<NonPublicStudentListItemModel[]>(async () => {
    if (calendarYear && calendarYear.includes('-')) {
      calendarYear = calendarYear.split('-')[1].trim();
    }
    return await NonPublicStudentServiceClient(config).get(
      nonPublicStudentApiPaths.getListItems(calendarYear)
    );
  });
};

export const getNonPublicStudentById = async (
  nonPublicStudentId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<NonPublicStudentEntryModel>> => {
  return executeApiRequest<NonPublicStudentEntryModel>(async () => {
    return await NonPublicStudentServiceClient(config).get(
      nonPublicStudentApiPaths.getById(nonPublicStudentId)
    );
  });
};

export const getNonPublicStudentScheduleFormViewModel = async (
  districtId: string | undefined,
  calendarYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<NonPublicStudentScheduleFormViewModel>> => {
  // Convert school year format if needed (2025-2026 -> 2026)
  let apiCalendarYear = calendarYear;
  if (calendarYear && calendarYear.includes('-')) {
    apiCalendarYear = calendarYear.split('-')[1].trim();
  }
  
  const response = await executeApiRequest<NonPublicStudentScheduleFormViewModel>(async () => {
    return await NonPublicStudentServiceClient(config).get(
      nonPublicStudentApiPaths.getScheduleForm(districtId, apiCalendarYear)
    );
  });
  
  if (response.status >= 400) {
    const defaultFormModel: NonPublicStudentScheduleFormViewModel = {
      defaultSchoolYear: calendarYear || new Date().getFullYear().toString(),
      extendedCourseCodeOptions: [],
      statusOptions: []
    };
    
    return {
      data: defaultFormModel,
      error: { type: '', title: '', status: 200, detail: '' },
      status: 200,
      aborted: false
    };
  }
  
  return response;
};

export const getNonPublicStudentFormViewModel = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<NonPublicStudentFormViewModel>> => {
  return executeApiRequest<NonPublicStudentFormViewModel>(async () => {
    return await NonPublicStudentServiceClient(config).get(
      nonPublicStudentApiPaths.getForm
    );
  });
};

export const getStudentByMSISId = async (
  msisId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<NonPublicStudentSearchResultModel>> => {
  return executeApiRequest<NonPublicStudentSearchResultModel>(async () => {
    return await NonPublicStudentServiceClient(config).get(
      nonPublicStudentApiPaths.getStudentByMsisId(msisId)
    );
  });
};

export const getSchedulesByNpsId = async (
  npsId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<NonPublicStudentScheduleListItemModel[]>> => {
  return executeApiRequest<NonPublicStudentScheduleListItemModel[]>(
    async () => {
      return await NonPublicStudentServiceClient(config).get(
        nonPublicStudentApiPaths.getSchedulesByNPSId(npsId)
      );
    }
  );
};

export const getScheduleByScheduleId = async (
  scheduleId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<NonPublicStudentScheduleModel>> => {
  return executeApiRequest<NonPublicStudentScheduleModel>(async () => {
    return await NonPublicStudentServiceClient(config).get(
      nonPublicStudentApiPaths.getByScheduleId(scheduleId)
    );
  });
};

export const deleteSchedule = async (
  scheduleId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  return executeApiRequest(async () => {
    return await NonPublicStudentServiceClient(config).delete(
      nonPublicStudentApiPaths.deleteSchedule(scheduleId)
    );
  });
};

export const deleteStudent = async (
  studentId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  return executeApiRequest(async () => {
    return await NonPublicStudentServiceClient(config).delete(
      nonPublicStudentApiPaths.deleteStudent(studentId)
    );
  });
};

export const getNpsSchoolYears = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await NonPublicStudentServiceClient(config).get(
      nonPublicStudentApiPaths.getSchoolYears
    );
  });
};
